import React, { useState, useEffect } from 'react';
import { getInlineLoader } from "../Helpers";

// Asset imports
import banner1 from '../assets/banner1.jpg'; // Mutton
import banner2 from '../assets/banner2.jpg'; // Avocados
import banner3 from '../assets/mangoes.jpeg';
import banner4 from '../assets/chilli.jpeg';
import banner5 from '../assets/pineapples.jpeg';
import banner6 from '../assets/bananas.jpeg';

const SLIDE_DATA = [
  { img: banner1, title: "Thai H&K Trading", sub: "Premium Tanzanian Mutton & Agricultural Exports", label: "Global Quality" },
  { img: banner2, title: "Fresh Avocados", sub: "Grade A Tanzanian Hass varieties for global markets", label: "Export Ready" },
  { img: banner3, title: "Tropical Mangoes", sub: "Sun-ripened, fiberless varieties sourced sustainably", label: "Farm Fresh" },
  { img: banner4, title: "Premium Chilli", description: "Hand-picked, high-pungency African Birds Eye chillies", label: "High Standards" },
  { img: banner5, title: "Sweet Pineapples", description: "Tanzania's finest high-brix pineapples for the Middle East", label: "Pure Quality" },
];

const ModernHeroSlider = ({ slides }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="hero-slider-wrapper mb-5 shadow-sm">
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`hero-slide ${i === index ? 'active' : ''}`}
          style={{ backgroundImage: `url(${slide.img})` }}
        >
          <div className="hero-overlay">
            <div className="container">
              <div className="slide-content text-start">
                <span className="badge bg-success mb-3 px-3 py-2 text-uppercase fw-bold ls-2 animate-item">
                  {slide.label}
                </span>
                <h1 className="display-2 fw-bold text-white mb-3 slide-title animate-item">
                  {slide.title}
                </h1>
                <p className="fs-4 text-white-50 slide-text animate-item" style={{ maxWidth: '700px' }}>
                  {slide.sub || slide.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      ))}
      
      <div className="hero-controls container">
        <div className="hero-indicators">
          {slides.map((_, i) => (
            <div 
              key={i} 
              className={`indicator-pill ${i === index ? 'active' : ''}`} 
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function AboutUsContent() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(true);
  }, []);

  if (!isReady) return <div className="p-5 text-center">{getInlineLoader()}</div>;

  return (
    <div className="MainBodySection p-0 bg-white">
      <style>{`
        .ls-2 { letter-spacing: 2px; }
        
        /* Full Width Slider Styles */
        .hero-slider-wrapper {
          position: relative;
          min-height: 100vh;
          background: #000;
          overflow: hidden;
          width: 100vw;
          margin-left: calc(-50vw + 50%);
          margin-top: 0;
        }

        .hero-slide {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          opacity: 0;
          transition: opacity 1.8s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1;
        }

        .hero-slide.active {
          opacity: 1;
          z-index: 2;
          animation: kenBurns 10s ease-out forwards;
        }

        @keyframes kenBurns {
          from { transform: scale(1.1); }
          to { transform: scale(1); }
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.1) 100%);
          display: flex;
          align-items: center;
        }

        .animate-item {
          opacity: 0;
          transform: translateY(30px);
          transition: 0.8s ease-out;
        }

        .hero-slide.active .animate-item {
          opacity: 1;
          transform: translateY(0);
        }

        .hero-slide.active .slide-title { transition-delay: 0.2s; }
        .hero-slide.active .slide-text { transition-delay: 0.4s; }

        .hero-controls {
          position: absolute;
          bottom: 50px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10;
        }

        .indicator-pill {
          width: 12px;
          height: 12px;
          background: rgba(255,255,255,0.3);
          cursor: pointer;
          transition: 0.4s;
          border-radius: 50%;
        }

        .indicator-pill.active {
          background: #28a745;
          transform: scale(1.3);
          box-shadow: 0 0 15px rgba(40, 167, 69, 0.5);
        }

        .hero-indicators {
            display: flex;
            gap: 15px;
        }

        .section-header-title {
          font-weight: 800;
          color: #1a3a14;
        }

        .value-card {
          border: 1px solid #f0f0f0;
          transition: 0.3s ease;
          border-radius: 20px;
        }

        .value-card:hover {
          background: #f8fafc;
          border-color: #28a745;
          transform: translateY(-5px);
        }
      `}</style>

      {/* 1. FULL WIDTH SLIDER */}
      <ModernHeroSlider slides={SLIDE_DATA} />

      {/* 2. CONTAINED CONTENT */}
      <div className="container py-5">
        
        {/* OVERVIEW */}
        <div className="row g-5 align-items-center mb-5">
          <div className="col-lg-6">
            <span className="text-success font-bold text-uppercase ls-2 small d-block mb-2">THAI H&K TRADING COMPANY</span>
            <h2 className="display-4 section-header-title text-primary mb-4">
              Tanzanian Excellence, <br/>Global Standards.
            </h2>
          </div>
          <div className="col-lg-6 border-start border-4 border-success ps-lg-5">
            <p className="fs-5 text-muted leading-relaxed">
              Thai H&K Trading Company is a premier agribusiness powerhouse. We specialize in the ethical 
              sourcing and hygienic processing of premium livestock and produce, ensuring every 
              export meets the rigorous demands of the international gold-chain management.
            </p>
          </div>
        </div>

        {/* MISSION & VISION */}
        <div className="row g-4 mb-5 pb-5">
          <div className="col-md-6">
            <div className="p-5 bg-dark text-white rounded-4 h-100 shadow-sm">
              <h3 className="fw-bold mb-3 text-success">Our Vision</h3>
              <p className="opacity-75 fs-5">To be the global leader in Tanzanian agricultural exports, recognized for setting the gold standard in premium food supply chain innovation.</p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="p-5 border rounded-4 h-100 shadow-sm">
              <h3 className="fw-bold mb-3 text-success">Our Mission</h3>
              <p className="text-muted fs-5">Providing the world with the highest quality Tanzanian produce through sustainable sourcing and a relentless focus on freshness and safety.</p>
            </div>
          </div>
        </div>

        {/* CORE VALUES */}
        <div className="text-center mb-5">
          <h2 className="display-6 fw-bold">Our Operating Values</h2>
          <div className="bg-success mx-auto mt-2" style={{height: '4px', width: '60px'}}></div>
        </div>

        <div className="row g-4 text-center pb-5">
          {[
            { icon: "🤝", title: "Integrity", text: "Transparent and honest transactions." },
            { icon: "🏆", title: "Excellence", text: "Settling for nothing less than premium quality." },
            { icon: "🌱", title: "Sustainability", text: "Supporting local farming and eco-friendly practices." },
            { icon: "🚛", title: "Reliability", text: "Superior logistics and gold-chain management." }
          ].map((item, idx) => (
            <div key={idx} className="col-lg-3 col-md-6">
              <div className="p-4 value-card h-100 shadow-sm">
                <div className="fs-1 mb-3">{item.icon}</div>
                <h4 className="fw-bold">{item.title}</h4>
                <p className="text-muted small mb-0">{item.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* COMPLIANCE BAR */}
        <div className="bg-light p-4 rounded-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-white p-2 rounded shadow-sm fw-bold px-3 small">HALAL CERTIFIED</div>
            <div className="bg-white p-2 rounded shadow-sm fw-bold px-3 small">TANZANIA MEAT BOARD</div>
          </div>
          <div className="text-muted small text-uppercase fw-bold ls-2">
            Fully Compliant with ISO Regulations
          </div>
        </div>

      </div>
    </div>
  );
}